import { OpenMarketsError } from "./errors.js";
import { accountPositionsSchema } from "./schemas/index.js";
const DEFAULT_MCP_URL = "https://api.openmarkets.ai/mcp";
/**
 * Parse a streamable-HTTP MCP response body. The server may reply with either a
 * plain JSON object or an SSE stream of `event:`/`data:` lines; we accept both and
 * return the last JSON-RPC object we can decode from a `data:` payload.
 */
function parseMcpBody(text) {
    const trimmed = text.trim();
    // Plain JSON (no SSE framing).
    if (trimmed.startsWith("{")) {
        try {
            return JSON.parse(trimmed);
        }
        catch {
            /* fall through to line parsing */
        }
    }
    let last = null;
    for (const line of trimmed.split(/\r?\n/)) {
        const l = line.trim();
        if (!l.startsWith("data:"))
            continue;
        const payload = l.slice(5).trim();
        if (!payload || payload === "[DONE]")
            continue;
        try {
            last = JSON.parse(payload);
        }
        catch {
            /* ignore non-JSON keepalive frames */
        }
    }
    if (!last) {
        throw new OpenMarketsError("MCP response had no parseable data frame", {
            code: "mcp_parse_error",
            status: 502,
            details: { sample: trimmed.slice(0, 200) },
        });
    }
    return last;
}
export class McpClient {
    apiKey;
    url;
    fetchImpl;
    rpcId = 0;
    constructor(opts) {
        if (!opts.apiKey)
            throw new Error("McpClient requires an apiKey");
        this.apiKey = opts.apiKey;
        this.url = opts.url ?? DEFAULT_MCP_URL;
        const f = opts.fetch ?? globalThis.fetch;
        if (!f)
            throw new Error("No fetch implementation available; pass { fetch } explicitly");
        this.fetchImpl = f;
    }
    /** Call a read tool and return its parsed text payload as unknown JSON. */
    async callTool(name, args = {}) {
        const body = {
            jsonrpc: "2.0",
            id: ++this.rpcId,
            method: "tools/call",
            params: { name, arguments: args },
        };
        let res;
        try {
            res = await this.fetchImpl(this.url, {
                method: "POST",
                headers: {
                    "X-API-Key": this.apiKey,
                    "Content-Type": "application/json",
                    Accept: "application/json, text/event-stream",
                },
                body: JSON.stringify(body),
            });
        }
        catch (cause) {
            throw OpenMarketsError.network(cause);
        }
        const text = await res.text();
        if (!res.ok) {
            throw new OpenMarketsError(`MCP call failed (${res.status})`, {
                code: "mcp_http_error",
                status: res.status,
                details: { tool: name, sample: text.slice(0, 200) },
            });
        }
        const rpc = parseMcpBody(text);
        if (rpc.error) {
            throw new OpenMarketsError(rpc.error.message ?? "MCP tool error", {
                code: "mcp_tool_error",
                status: 502,
                details: { tool: name, ...rpc.error },
            });
        }
        const content = rpc.result?.content ?? [];
        const textNode = content.find((c) => typeof c.text === "string");
        if (!textNode?.text) {
            throw new OpenMarketsError("MCP tool returned no text content", {
                code: "mcp_empty_content",
                status: 502,
                details: { tool: name },
            });
        }
        let parsed;
        try {
            parsed = JSON.parse(textNode.text);
        }
        catch (cause) {
            throw new OpenMarketsError("MCP tool text was not JSON", {
                code: "mcp_content_not_json",
                status: 502,
                details: { tool: name, sample: textNode.text.slice(0, 200), cause: String(cause) },
            });
        }
        // Account tools wrap their result in a `{ data, meta? }` envelope.
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed) && "data" in parsed) {
            return parsed.data;
        }
        return parsed;
    }
    /** Rolled-up account positions (the one account read with no REST equivalent). */
    async listAccountPositions() {
        const data = await this.callTool("list_account_positions");
        return validate(accountPositionsSchema, data);
    }
}
function validate(schema, raw) {
    const parsed = schema.safeParse(raw);
    // Warn-parity with the HTTP client: hand back raw on mismatch rather than throw,
    // so drift surfaces in the UI instead of breaking the page.
    return parsed.success ? parsed.data : raw;
}
//# sourceMappingURL=mcp.js.map